import FilepondPluginFileValidateSize from 'filepond-plugin-file-validate-size/dist/filepond-plugin-file-validate-size.esm.js';
import { registerPlugin } from 'ngx-filepond';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatPaginator, MatTableDataSource } from '@angular/material';
import { RESTDataSource } from '../../../helpers/rest.datasource';
import { OfferIface } from '../../../interfaces/offer.iface';
import { Subscription } from 'rxjs/Subscription';
import { Tag } from '../../../interfaces/tag';
import { OffersService } from '../../../services/offers.service';
import { LoggerService } from '../../../services/logger.service';
import { AuthService } from '../../../services/auth.service';
import { tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomerSegmentsService } from '../../../services/customer-segments.service';
import { CustomerSegmentsIface } from '../../../interfaces/customer-segments.iface';
import { ToastsManager } from 'ng2-toastr';
import * as FileSaver from 'file-saver';
import { RegulationsService } from '../../../services/regulations.service';
import { TranslateService } from '@ngx-translate/core';
import { CookieService } from 'ng2-cookies';
import { TagsServiceTypeEnum } from '../../../enums/tags-service-type.enum';

registerPlugin(FilepondPluginFileValidateSize);

@Component({
  selector: 'app-customer-segment-edit',
  templateUrl: 'customer-segment.component-edit.html',
  styleUrls: ['./customer-segment-edit.component.scss']
})
export class CustomerSegmentEditComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(MatPaginator)
  paginator: MatPaginator;
  @ViewChild('myPond')
  myPond: any;
  public readonly tagsServiceTypeEnum = TagsServiceTypeEnum;
  public dataSource: RESTDataSource<OfferIface>;
  public chosenOffersDataSource: MatTableDataSource<OfferIface>;
  public displayedColumns = [
    'action',
    'name',
    'availableInLocations',
    'availableInChannels',
    'availableForCustomerSegments',
    'code',
    'price',
    'period'
  ];
  public customerSegment: CustomerSegmentsIface = {
    name: '',
    filename: '',
    offerIds: [],
    customers: []
  };
  public chosenOffers: OfferIface[] = [];
  public isLoadingResults = false;
  public subscription: Subscription;
  public activeTags: Tag[] = [];
  public serviceName = OffersService.name;

  public id: string;

  constructor(
    private authService: AuthService,
    private offersService: OffersService,
    private customerSegmentsService: CustomerSegmentsService,
    private route: ActivatedRoute,
    private router: Router,
    private toastr: ToastsManager,
    private cookieService: CookieService,
    public translate: TranslateService
  ) {}

  ngOnInit() {
    LoggerService.log('CustomerSegmentEditComponent::onInit');
    this.route.params.subscribe(params => {
      this.id = params['id'];
      if (this.id) {
        this.customerSegmentsService.findById(this.id).subscribe(
          segment => {
            this.customerSegment.filename = segment.filename;
            this.customerSegment.name = segment.name;
            this.customerSegment.customers = segment.customers;
            this.offersService
              .findAll(
                {
                  _id: { $in: segment.offerIds },
                  offerStatus: { $ne: 'withdrawn' },
                  availableUntil: { $gte: Date.now() }
                },
                0,
                0
              )
              .subscribe(offers => {
                this.chosenOffers = offers;
                this.chosenOffersDataSource = new MatTableDataSource(this.chosenOffers);
                this.loadData();
              });
          },
          () => {
            this.router.navigate(['/customer-segments']);
          }
        );
      }
    });

    this.dataSource = new RESTDataSource(this.offersService);
    this.subscription = this.dataSource.loading$.subscribe(state => {
      this.isLoadingResults = state;
    });
    this.paginator.pageSize = 10;
    this.loadData();
  }

  ngAfterViewInit() {
    LoggerService.log('CustomerSegmentEditComponent::ngAfterViewInit');
    this.paginator.page.pipe(tap(() => this.loadData())).subscribe();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  loadData(): void {
    let query = {
      _id: { $nin: this.chosenOffers.map(o => o._id) },
      offerStatus: { $ne: 'withdrawn' },
      availableUntil: { $gte: Date.now() }
    };
    const activeTags = this.activeTags.map(activeTag => activeTag.value);
    if (activeTags.length) {
      query = Object.assign(query, { 'tags.value': { $all: activeTags } });
    }
    this.dataSource.loadData(query, this.paginator.pageIndex, this.paginator.pageSize, '');
  }

  chooseOffer(offer: OfferIface): void {
    const exists = this.chosenOffers.some(o => o._id === offer._id);
    if (!exists) {
      this.chosenOffers.push(offer);
      this.chosenOffersDataSource = new MatTableDataSource(this.chosenOffers);
      this.loadData();
    }
  }

  chooseAllOffers(): void {
    const activeTags = this.activeTags.map(activeTag => activeTag.value);
    const query = { 'tags.value': { $all: activeTags } };
    this.offersService.findAll(query, 0, 0).subscribe(offers => {
      this.chosenOffers = [
        ...this.chosenOffers,
        ...offers.filter(offer => {
          return this.chosenOffers.every(element => {
            return element._id !== offer._id;
          });
        })
      ];
      this.chosenOffersDataSource = new MatTableDataSource(this.chosenOffers);
      this.loadData();
    });
  }

  deleteOffer(index: number): void {
    this.chosenOffers.splice(index, 1);
    this.chosenOffersDataSource = new MatTableDataSource(this.chosenOffers);
    this.loadData();
  }

  saveSegment(): void {
    this.myPond.options = this.getUploadConfig();
    if (this.id && !this.myPond.getFile()) {
      const segment: CustomerSegmentsIface = {
        name: this.customerSegment.name,
        offerIds: this.chosenOffers.map(offer => offer._id),
        customers: this.customerSegment.customers
      };
      this.customerSegmentsService.update(this.id, segment).subscribe(
        () => {
          this.router.navigate(['/customer-segments']).then(() => {
            this.toastr.info(this.translate.instant('CUSTOMER_SEGMENT_EDIT.COMPONENT.SEGMENT_SAVED'));
          });
        },
        () => {
          this.toastr.error(
            this.translate.instant('CUSTOMER_SEGMENT_EDIT.COMPONENT.TRY_AGAIN'),
            this.translate.instant('CUSTOMER_SEGMENT_EDIT.COMPONENT.FAILED_TO_SAVE')
          );
        }
      );
    } else {
      this.myPond.getFile().setMetadata('metadata', {
        name: this.customerSegment.name,
        offerIds: this.chosenOffers.map(offer => offer._id)
      });
      this.myPond.processFile();
    }
  }

  getFile(filename: string): void {
    this.customerSegmentsService.getFile(filename).subscribe(
      file => {
        if (file.hasOwnProperty('data')) {
          const { data, type } = file;
          const blob = RegulationsService.b64toBlob(Buffer.from(data).toString('base64'), type);
          FileSaver.saveAs(blob, filename, true);
          this.toastr.success(this.translate.instant('CUSTOMER_SEGMENT_EDIT.COMPONENT.FILE_DOWNLOADED'));
        }
      },
      err => {
        if (err.status === 404) {
          this.toastr.error(
            this.translate.instant('CUSTOMER_SEGMENT_EDIT.COMPONENT.FILE_MISSING'),
            this.translate.instant('CUSTOMER_SEGMENT_EDIT.COMPONENT.WARNING')
          );
        }
      }
    );
  }

  filterByTags(tags: Tag[]): void {
    this.paginator.firstPage();
    this.activeTags = tags;
    this.loadData();
  }

  getUploadConfig(): object {
    const url = this.id ? `/${this.id}` : '/';
    return {
      class: 'my-filepond',
      name: 'file',
      multiple: false,
      maxFileSize: '2MB',
      instantUpload: 'false',
      labelIdle: this.translate.instant('CUSTOMER_SEGMENT_EDIT.COMPONENT.POND_LABELS.DROP_FILE_HERE'),
      labelFileProcessing: this.translate.instant('CUSTOMER_SEGMENT_EDIT.COMPONENT.POND_LABELS.PROCESSING'),
      labelFileProcessingComplete: this.translate.instant(
        'CUSTOMER_SEGMENT_EDIT.COMPONENT.POND_LABELS.PROCESSING_COMPLETE'
      ),
      labelFileProcessingAborted: this.translate.instant(
        'CUSTOMER_SEGMENT_EDIT.COMPONENT.POND_LABELS.PROCESSING_ABORTED'
      ),
      labelFileProcessingError: this.translate.instant(
        'CUSTOMER_SEGMENT_EDIT.COMPONENT.POND_LABELS.FILE_PROCESSING_ERROR'
      ),
      labelTapToCancel: this.translate.instant('CUSTOMER_SEGMENT_EDIT.COMPONENT.POND_LABELS.TAP_TO_CANCEL'),
      labelTapToUndo: this.translate.instant('CUSTOMER_SEGMENT_EDIT.COMPONENT.POND_LABELS.TAP_TO_UNDO'),
      labelTapToRetry: this.translate.instant('CUSTOMER_SEGMENT_EDIT.COMPONENT.POND_LABELS.TAP_TO_RETRY'),
      labelFileTypeNotAllowed: this.translate.instant('CUSTOMER_SEGMENT_EDIT.COMPONENT.POND_LABELS.INVALID_TYPE'),
      labelMaxFileSize: this.translate.instant('CUSTOMER_SEGMENT_EDIT.COMPONENT.POND_LABELS.MAX_FILE_SIZE'),
      labelMaxFileSizeExceeded: this.translate.instant('CUSTOMER_SEGMENT_EDIT.COMPONENT.POND_LABELS.FILE_TO_BIG'),
      server: {
        url: environment.CUSTOMER_SEGMENTS_API_URL,
        process: {
          url,
          headers: {
            Authorization: this.authService.token,
            'X-XSRF-TOKEN': this.cookieService.get('XSRF-TOKEN')
          },
          withCredentials: true,
          onload: () => {
            this.router.navigate(['/customer-segments']).then(() => {
              this.toastr.info(this.translate.instant('CUSTOMER_SEGMENT_EDIT.COMPONENT.POND_LABELS.SEGMENT_SAVED'));
            });
          },
          onerror: () => {
            this.toastr.error(
              this.translate.instant('CUSTOMER_SEGMENT_EDIT.COMPONENT.POND_LABELS.TRY_AGAIN'),
              this.translate.instant('CUSTOMER_SEGMENT_EDIT.COMPONENT.POND_LABELS.FAILED_TO_LOAD')
            );
          }
        }
      }
    };
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/customer-segments/customer-segment-edit/customer-segment-edit.component.ts